import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ContentPage, ContentSection } from '@/components/marketing/content-page';
import { ContentFaq } from '@/components/marketing/content-faq';
import {
  setDocumentMeta, setCanonical, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildItemListSchema, buildBreadcrumbSchema,
} from '@/lib/seo';

const FAQS = [
  {
    q: 'What is help desk software used for?',
    a: 'Help desk software is used to receive, organize, prioritize, track, and resolve requests — usually support tickets from customers or IT requests from employees. It replaces shared inboxes and spreadsheets with a single system where every request has an owner, a status, a priority, and a history, so nothing is lost and response times can be measured.',
  },
  {
    q: 'What is the best help desk software for small business?',
    a: 'For small businesses, the best help desk software is one with a usable free start and predictable costs as the team grows: Kora (pay-as-you-go credits ($1 = 10 credits, 1 credit per ticket, 1,100 free on signup, unlimited members)), Freshdesk (from $19/agent/month), and Zoho Desk (free for 3 users) are the most common starting points. The deciding factors are usually AI features, per-seat pricing, and setup time.',
  },
  {
    q: 'What are examples of help desk software in 2026?',
    a: 'Widely used examples in 2026 include Zendesk, Freshdesk, Zoho Desk, Help Scout, and Intercom for customer support; ServiceNow, Jira Service Management, and ManageEngine for IT service desks; Jira, Linear, and GitHub Issues for development issue tracking; and AI-native tools like Kora that structure tickets automatically.',
  },
];

const EXAMPLE_GROUPS = [
  {
    category: 'Customer support help desks',
    description:
      'Built for support teams answering customer tickets across email, chat, and social channels.',
    examples: [
      { name: 'Zendesk', detail: 'The enterprise standard. Suite Team starts at $55/agent/month; costs scale per seat.', path: '/compare/kora-vs-zendesk' },
      { name: 'Freshdesk', detail: 'Freshworks\u2019 helpdesk. Growth at $19/agent/month on annual billing.', path: '/compare/kora-vs-freshdesk' },
      { name: 'Zoho Desk', detail: 'Budget-friendly, bundled with the Zoho ecosystem. Free for 3 users.', path: '/compare/kora-vs-zoho-desk' },
      { name: 'Help Scout', detail: 'Email-first shared inbox with a clean interface. Free plan for up to 5 users and 100 contacts.', path: '/compare/kora-vs-help-scout' },
    ],
  },
  {
    category: 'IT service desks (ITSM)',
    description:
      'Built for internal IT teams managing incidents, service requests, and SLAs.',
    examples: [
      { name: 'ServiceNow', detail: 'Enterprise ITSM platform with CMDB and workflow automation.', path: '/compare/kora-vs-servicenow' },
      { name: 'Jira Service Management', detail: 'Atlassian\u2019s ITSM offering, popular with dev-centric organizations.', path: '/compare/kora-vs-jira-service-management' },
      { name: 'ManageEngine ServiceDesk', detail: 'Mid-market ITSM with asset management included.', path: '/compare/kora-vs-manageengine-servicedesk' },
    ],
  },
  {
    category: 'Development issue trackers',
    description:
      'Built for engineering teams tracking bugs, feature requests, and sprint work.',
    examples: [
      { name: 'Jira', detail: 'The default agile project tracker for engineering teams.', path: '/compare/kora-vs-jira' },
      { name: 'Linear', detail: 'Fast, opinionated issue tracking for product teams.', path: '/compare/kora-vs-linear' },
      { name: 'GitHub Issues', detail: 'Issue tracking built into every GitHub repository.', path: '/compare/kora-vs-github-issues' },
    ],
  },
  {
    category: 'AI-native help desks',
    description:
      'A 2026 category where AI structures and prioritizes every ticket.',
    examples: [
      { name: 'Kora', detail: 'Free AI ticket structuring and an AI Analyst (1 credit per answer). Pay-as-you-go credits: $1 = 10 credits, 1 credit per ticket, 1,100 free on signup.', path: '/' },
    ],
  },
  {
    category: 'Open-source help desks',
    description: 'Self-hosted options for teams that want full data ownership.',
    examples: [
      { name: 'osTicket', detail: 'Classic PHP ticketing system, free to self-host.', path: '/compare/kora-vs-osticket' },
      { name: 'FreeScout', detail: 'Lightweight shared inbox, self-hosted alternative to Zendesk.', path: '/compare/kora-vs-groove' },
    ],
  },
];

export function HelpDeskSoftwareExamplesPage() {
  useEffect(() => {
    const title = 'Help Desk Software Examples — 5 Categories Compared (2026)';
    const description =
      'Real help desk software examples across customer support, ITSM, issue tracking, AI-native, and open source — with pricing and who each is for.';
    setDocumentMeta(title, description);
    setCanonical('/help-desk-software-examples');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/help-desk-software-examples', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app' },
        { name: 'Help Desk Software Examples', url: 'https://usekora.app/help-desk-software-examples' },
      ]),
      buildItemListSchema(
        EXAMPLE_GROUPS.flatMap((g) =>
          g.examples.map((e) => ({ name: e.name, url: `https://usekora.app${e.path}` })),
        ),
      ),
      buildFaqPageSchema(FAQS),
    ]);
    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Guide · Updated September 2026"
      title="Help desk software examples"
      subtitle="Concrete examples of help desk software across five categories, with pricing and who each one fits."
      answer={
        <p>
          Help desk software examples span five categories. Customer support help desks include{' '}
          <strong>Zendesk</strong> (Suite Team from $55/agent/month), <strong>Freshdesk</strong>{' '}
          (Growth from $19/agent/month), and <strong>Zoho Desk</strong>. IT service desks include{' '}
          <strong>ServiceNow</strong>, <strong>Jira Service Management</strong>, and{' '}
          <strong>ManageEngine</strong>. Development issue trackers include <strong>Jira</strong>,{' '}
          <strong>Linear</strong>, and <strong>GitHub Issues</strong>. Open-source options include{' '}
          <strong>osTicket</strong>. A newer AI-native category — including <strong>Kora</strong> —
          uses AI to structure and prioritize every ticket automatically, with pricing that is
          often usage-based rather than per-seat.
        </p>
      }
    >
      <ContentSection title="Customer support help desks">
        <p>
          These tools are built for teams answering customer questions. Zendesk is the enterprise
          standard with the deepest feature set — and per-agent pricing that scales with headcount.{' '}
          Freshdesk offers a friendlier entry point with Growth at $19/agent/month. Zoho Desk bundles well if you already use Zoho.
          Help Scout keeps things email-first and simple.
        </p>
        <p>
          The main cost pattern to know: most charge per agent per month, so a 10-agent team pays
          10× the per-seat price. Kora is the notable exception — pay-as-you-go credits with unlimited
          members and no seat fees, with AI ticket structuring free.
        </p>
      </ContentSection>

      <ContentSection title="IT service desks (ITSM)">
        <p>
          ITSM tools manage incidents, service requests, changes, and SLAs for internal IT teams.
          ServiceNow dominates enterprise IT; Jira Service Management is the pick for
          Atlassian-running organizations; ManageEngine ServiceDesk fits mid-market teams that want
          asset management included. These tools are heavier to configure and typically require an
          administrator.
        </p>
        <p>
          For small IT teams, full ITSM platforms are often overkill — a lightweight issue tracker
          with categories, priorities, and an API covers most internal workflows.
        </p>
      </ContentSection>

      <ContentSection title="Development issue trackers">
        <p>
          Engineering teams track bugs and feature work in Jira, Linear, or GitHub Issues. These
          tools optimize for sprint workflows and developer experience rather than customer
          communication. Ops and support teams that need to track both customer tickets and
          internal issues often end up paying for two systems — one reason AI-native help desks
          that handle messy input from any source are growing.
        </p>
      </ContentSection>

      <ContentSection title="AI-native help desks">
        <p>
          The newest category. Instead of bolting an AI chatbot onto a legacy helpdesk, AI-native
          tools put AI at intake: paste a Slack thread, WhatsApp forward, or customer email, and
          the system writes a clean title, picks the category, and sets priority. Kora does this in
          seconds at no extra charge. Others
          in this space charge for AI as an add-on — some vendors bill per AI resolution.
        </p>
      </ContentSection>

      <ContentSection title="Open-source help desks">
        <p>
          osTicket and FreeScout are the long-standing self-hosted options. They trade convenience
          for control: you run the server, apply updates, and manage backups. They suit teams with
          hosting expertise and strict data-residency requirements.
        </p>
      </ContentSection>

      <ContentSection title="Quick comparison">
        <div className="rounded-xl border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Tool</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Category</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Pricing model</th>
              </tr>
            </thead>
            <tbody>
              {EXAMPLE_GROUPS.flatMap((g) =>
                g.examples.map((e) => (
                  <tr key={e.name} className="border-b border-zinc-100 last:border-0">
                    <td className="px-4 py-3 font-medium text-zinc-900 ">
                      <Link to={e.path} className="hover:underline">{e.name}</Link>
                    </td>
                    <td className="px-4 py-3 text-zinc-500 ">{g.category}</td>
                    <td className="px-4 py-3 text-zinc-500 ">{e.detail}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      </ContentSection>

      <ContentFaq faqs={FAQS} subtitle="Questions people ask about help desk software examples." />
    </ContentPage>
  );
}
